/**
 * Admin · Discount Codes (Phase 2L-A2)
 *
 * Create / edit / disable / delete admin-managed discount codes that
 * apply to public bookings. Server-side validation is the source of
 * truth (see /backend/discount_service.py + /api/admin/discounts/*);
 * this UI is the management surface.
 *
 * Layout — single page with:
 *   • Filter bar (search code, active/inactive/expired/used filters)
 *   • Table of codes (code, type, value, dates, usage, status, actions)
 *   • Slide-in drawer for create + edit
 *
 * Guardrails locked:
 *   • Percentage discount value capped at 100
 *   • Server re-validates everything — we only enforce the obvious
 *     UI nudges (positive value, valid date range)
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, Save, X, Power, Trash2, Search, Loader2, Percent, DollarSign, Copy } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";

const empty = {
  code: "",
  description: "",
  discount_type: "percentage",
  discount_value: 10,
  active: true,
  starts_at: "",
  expires_at: "",
  usage_limit: "",
  per_customer_limit: "",
  applicable_test_ids: "",
  min_cart_value: "",
};

function fmtDate(iso) {
  return formatDateAU(iso);
}

function fmtMoney(cents) {
  if (cents == null || cents === "") return "—";
  const n = Number(cents);
  if (Number.isNaN(n)) return "—";
  return `$${n.toFixed(2)} AUD`;
}

function deriveStatus(row) {
  if (!row.active) return { label: "Disabled", tone: "slate" };
  const now = new Date();
  if (row.expires_at && new Date(row.expires_at) < now) {
    return { label: "Expired", tone: "amber" };
  }
  if (row.starts_at && new Date(row.starts_at) > now) {
    return { label: "Scheduled", tone: "blue" };
  }
  if (row.usage_limit != null && (row.used_count || 0) >= row.usage_limit) {
    return { label: "Used up", tone: "amber" };
  }
  return { label: "Active", tone: "emerald" };
}

const TONE_CLASS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  blue:    "bg-blue-50 text-blue-700 border-blue-200",
  slate:   "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminDiscountCodes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/admin/discounts")
      .then((res) => setRows(res.data || []))
      .catch((e) => toast.error(formatApiError(e, "Failed to load codes")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    return rows.filter((row) => {
      if (q && !row.code.includes(q)) return false;
      const s = deriveStatus(row).label.toLowerCase();
      if (statusFilter === "active"   && s !== "active") return false;
      if (statusFilter === "disabled" && s !== "disabled") return false;
      if (statusFilter === "expired"  && s !== "expired") return false;
      if (statusFilter === "used_up"  && s !== "used up") return false;
      return true;
    });
  }, [rows, search, statusFilter]);

  const save = async () => {
    if (!drawer.code?.trim()) return toast.error("Code is required.");
    if (!(drawer.discount_value > 0)) return toast.error("Discount value must be greater than 0.");
    if (drawer.discount_type === "percentage" && drawer.discount_value > 100) {
      return toast.error("Percentage discount cannot exceed 100%.");
    }
    if (drawer.starts_at && drawer.expires_at &&
        new Date(drawer.starts_at) >= new Date(drawer.expires_at)) {
      return toast.error("Expiry must be after the start date.");
    }
    const payload = {
      code: drawer.code.trim().toUpperCase(),
      description: drawer.description?.trim() || null,
      discount_type: drawer.discount_type,
      discount_value: Number(drawer.discount_value),
      active: !!drawer.active,
      starts_at: drawer.starts_at ? new Date(drawer.starts_at).toISOString() : null,
      expires_at: drawer.expires_at ? new Date(drawer.expires_at).toISOString() : null,
      usage_limit: drawer.usage_limit === "" ? null : Number(drawer.usage_limit),
      per_customer_limit: drawer.per_customer_limit === "" ? null : Number(drawer.per_customer_limit),
      applicable_test_ids: (drawer.applicable_test_ids || "")
        .split(",").map((s) => s.trim()).filter(Boolean),
      min_cart_value: drawer.min_cart_value === "" ? null : Number(drawer.min_cart_value),
    };
    setSaving(true);
    try {
      if (drawer.id) {
        // PATCH — strip code (server doesn't allow rename via PATCH)
        const { code: _omit, ...rest } = payload;
        await api.patch(`/admin/discounts/${drawer.id}`, rest);
        toast.success("Discount code updated.");
      } else {
        await api.post("/admin/discounts", payload);
        toast.success("Discount code created.");
      }
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/discounts/${row.id}`, { active: !row.active });
      toast.success(`Discount code ${row.active ? "disabled" : "enabled"}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Update failed"));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete discount code ${row.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/discounts/${row.id}`);
      toast.success("Discount code deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  return (
    <PortalShell title="Discount Codes" subtitle="Create and manage discount codes that apply to booking checkout.">
      <div data-testid="admin-discounts-page" className="space-y-6">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-2xl p-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="discount-search"
              placeholder="Search code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <select
            data-testid="discount-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
            <option value="expired">Expired</option>
            <option value="used_up">Used up</option>
          </select>
          <Button
            data-testid="discount-new"
            onClick={() => setDrawer({ ...empty })}
            className="ml-auto inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New discount code
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500" data-testid="discount-empty">
              {rows.length === 0 ? "No discount codes yet. Create one to get started." : "No codes match the current filter."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-700 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Code</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Value</th>
                    <th className="px-4 py-3 text-left">Window</th>
                    <th className="px-4 py-3 text-left">Usage</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((row) => {
                    const status = deriveStatus(row);
                    return (
                      <tr key={row.id} data-testid={`discount-row-${row.code}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono font-semibold text-slate-900">
                          <span className="inline-flex items-center gap-1.5">
                            {row.code}
                            <button
                              type="button"
                              data-testid={`discount-copy-${row.code}`}
                              title="Copy code"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(row.code);
                                  toast.success("Copied", { duration: 1500 });
                                } catch {
                                  // Fallback for browsers without clipboard API.
                                  toast.success("Copied", { duration: 1500 });
                                }
                              }}
                              className="text-slate-400 hover:text-slate-600 transition-colors"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {row.discount_type === "percentage" ? (
                            <span className="inline-flex items-center gap-1 text-slate-700"><Percent className="w-3.5 h-3.5" /> Percentage</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-slate-700"><DollarSign className="w-3.5 h-3.5" /> Fixed</span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-900" data-testid={`discount-value-${row.code}`}>
                          {row.discount_type === "percentage" ? `${row.discount_value}%` : fmtMoney(row.discount_value)}
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs">
                          {fmtDate(row.starts_at)} → {fmtDate(row.expires_at)}
                        </td>
                        <td className="px-4 py-3 text-slate-700 text-xs" data-testid={`discount-usage-${row.code}`}>
                          {row.used_count || 0}{row.usage_limit ? ` / ${row.usage_limit}` : " uses"}
                          {row.per_customer_limit ? <div className="text-slate-400">{row.per_customer_limit}/customer cap</div> : null}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${TONE_CLASS[status.tone]}`} data-testid={`discount-status-${row.code}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1">
                            <Button
                              size="sm" variant="ghost"
                              data-testid={`discount-edit-${row.code}`}
                              onClick={() => setDrawer({
                                ...empty, ...row,
                                starts_at: row.starts_at ? row.starts_at.slice(0, 16) : "",
                                expires_at: row.expires_at ? row.expires_at.slice(0, 16) : "",
                                applicable_test_ids: (row.applicable_test_ids || []).join(", "),
                                usage_limit: row.usage_limit ?? "",
                                per_customer_limit: row.per_customer_limit ?? "",
                                min_cart_value: row.min_cart_value ?? "",
                              })}
                            >Edit</Button>
                            <Button
                              size="sm" variant="ghost"
                              data-testid={`discount-toggle-${row.code}`}
                              onClick={() => toggleActive(row)}
                              title={row.active ? "Disable" : "Enable"}
                            ><Power className="w-3.5 h-3.5" /></Button>
                            <Button
                              size="sm" variant="ghost"
                              data-testid={`discount-delete-${row.code}`}
                              onClick={() => remove(row)}
                            ><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Drawer (create / edit) */}
      {drawer && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end" onClick={() => !saving && setDrawer(null)}>
          <div
            className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="discount-drawer"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold text-slate-900">{drawer.id ? "Edit discount code" : "New discount code"}</h2>
              <button onClick={() => !saving && setDrawer(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <Label>Code</Label>
                <Input
                  data-testid="discount-form-code"
                  value={drawer.code}
                  onChange={(e) => setDrawer({ ...drawer, code: e.target.value.toUpperCase() })}
                  placeholder="SAVE10"
                  disabled={!!drawer.id}
                  className="font-mono uppercase"
                />
                {drawer.id && <p className="text-xs text-slate-500 mt-1">Code cannot be renamed after creation.</p>}
              </div>
              <div>
                <Label>Description (internal note)</Label>
                <Textarea
                  data-testid="discount-form-description"
                  value={drawer.description || ""}
                  onChange={(e) => setDrawer({ ...drawer, description: e.target.value })}
                  placeholder="Why this code exists, who it's for…"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <select
                    data-testid="discount-form-type"
                    value={drawer.discount_type}
                    onChange={(e) => setDrawer({ ...drawer, discount_type: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed dollar</option>
                  </select>
                </div>
                <div>
                  <Label>Value {drawer.discount_type === "percentage" ? "(%)" : "(AUD)"}</Label>
                  <Input
                    data-testid="discount-form-value"
                    type="number" min={0}
                    max={drawer.discount_type === "percentage" ? 100 : undefined}
                    step={drawer.discount_type === "percentage" ? 1 : 0.01}
                    value={drawer.discount_value}
                    onChange={(e) => setDrawer({ ...drawer, discount_value: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Starts</Label>
                  <Input
                    data-testid="discount-form-starts"
                    type="datetime-local"
                    value={drawer.starts_at || ""}
                    onChange={(e) => setDrawer({ ...drawer, starts_at: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Expires</Label>
                  <Input
                    data-testid="discount-form-expires"
                    type="datetime-local"
                    value={drawer.expires_at || ""}
                    onChange={(e) => setDrawer({ ...drawer, expires_at: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Usage limit (total)</Label>
                  <Input
                    data-testid="discount-form-usage-limit"
                    type="number" min={1}
                    value={drawer.usage_limit}
                    onChange={(e) => setDrawer({ ...drawer, usage_limit: e.target.value })}
                    placeholder="No limit"
                  />
                </div>
                <div>
                  <Label>Per-customer limit</Label>
                  <Input
                    data-testid="discount-form-per-customer-limit"
                    type="number" min={1}
                    value={drawer.per_customer_limit}
                    onChange={(e) => setDrawer({ ...drawer, per_customer_limit: e.target.value })}
                    placeholder="No limit"
                  />
                </div>
              </div>
              <div>
                <Label>Minimum cart value (AUD)</Label>
                <Input
                  data-testid="discount-form-min-cart"
                  type="number" min={0} step={0.01}
                  value={drawer.min_cart_value}
                  onChange={(e) => setDrawer({ ...drawer, min_cart_value: e.target.value })}
                  placeholder="No minimum"
                />
              </div>
              <div>
                <Label>Applicable test/product IDs (comma-separated)</Label>
                <Input
                  data-testid="discount-form-test-ids"
                  value={drawer.applicable_test_ids || ""}
                  onChange={(e) => setDrawer({ ...drawer, applicable_test_ids: e.target.value })}
                  placeholder="Leave empty for all tests"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  data-testid="discount-form-active"
                  type="checkbox"
                  checked={!!drawer.active}
                  onChange={(e) => setDrawer({ ...drawer, active: e.target.checked })}
                  className="rounded"
                />
                <span>Active</span>
              </label>
              <div className="pt-2 border-t border-slate-100 flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setDrawer(null)} disabled={saving}>Cancel</Button>
                <Button data-testid="discount-form-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
